// @ts-nocheck
import Editor from '../../Editor/Editor';
import {
  CalendarBlank,
  Clock,
  FileText,
  MapPin,
  Phone,
  ArrowSquareOut,
  ForkKnife,
  AirplaneTakeoff
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import {
  formatTimeLocale,
  formatDurationDisplay
} from '../../../utils/dateUtility';
import { formatTravelDate } from '../../../utils/travelDate';
import { useTimeFormat } from '../../../hooks/useTimeFormat';
import { openMaps } from '../../../pwa/TravelerApp/helpers/contact';
import { getLineItemAttachmentUrl } from '../../../utils/resourcePathHelper';
import { sortMealOptions } from '../../../constants/lineItemFieldConfig';
import PropTypes from 'prop-types';

/**
 * TravelerAppLineItemDisplay - Display component for LineItems in Traveler App / Final Itinerary
 * Copied from PWA ServiceCard components and adapted to use LineItem data
 */
const TravelerAppLineItemDisplay = ({
  lineItem,
  isPreview,
  isDesktop,
  isCheckout = false
}) => {
  const { t } = useTranslation(['services']);
  const { timeFormat } = useTimeFormat();

  // Helper function to check if a value has meaningful content (not just whitespace)
  const hasValue = (value) => {
    return value && typeof value === 'string' && value.trim() !== '';
  };

  const {
    lineitemid,
    servicetypecode,
    title,
    description,
    vendorname,
    vendorphone,
    vendorlink,
    startlocation,
    endlocation,
    starttime,
    endtime,
    durationtime,
    confirmationnumber,
    serviceclass,
    servicenumber,
    mealsincluded,
    contactname,
    startDate,
    endDate,
    enddayoffset,
    lineitemattachment = []
  } = lineItem || {};

  // Build attachments
  const attachments = lineitemattachment
    .sort((a, b) => a.seq - b.seq)
    .map((att) => {
      if (att.resourcepath) {
        return {
          name: att.attachmentname || 'Attachment',
          url: getLineItemAttachmentUrl({ resourcePath: att.resourcepath })
        };
      }
      return null;
    })
    .filter(Boolean);

  // Parse meals included
  const mealsArray = mealsincluded ? sortMealOptions(mealsincluded) : [];

  // Handle checkout LineItems with simple display
  if (isCheckout) {
    return (
      <div>
        <div className='activity-card__header'>
          <h3 className='activity-card__title'>
            {t('services:checkOutLabel', 'Check Out')}
          </h3>
        </div>
        <div className='activity-card__description-container'>
          <p className='activity-card__description'>
            {hasValue(vendorname) ? vendorname : hasValue(title) ? title : ''}
          </p>
        </div>
        {hasValue(endtime) && (
          <div className='activity-card__modal-field__item'>
            <Clock size={16} color='#667387' />
            <p className='activity-card__modal-field__value'>
              {formatTimeLocale(endtime)}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Render service-specific content based on type
  const renderServiceContent = () => {
    switch (servicetypecode) {
      case 'HTL':
        return renderHotelContent();
      case 'FLT':
        return renderFlightContent();
      case 'ACT':
        return renderActivityContent();
      case 'RST':
        return renderRestaurantContent();
      case 'TRF':
        return renderTransferContent();
      case 'CAR':
        return renderCarContent();
      case 'TRN':
        return renderTrainContent();
      case 'INF':
        return renderInfoContent();
      default:
        return null;
    }
  };

  const renderHotelContent = () => {
    if (isPreview) {
      return (
        <>
          <div className='activity-card__header'>
            <h3 className='activity-card__title'>
              {hasValue(title) ? title : ''}
            </h3>
          </div>

          {hasValue(starttime) && (
            <div className='activity-card__time'>
              <Clock size={14} weight='regular' />
              <span>{formatTimeLocale(starttime, timeFormat)}</span>
            </div>
          )}

          <div className='activity-card__description-container'>
            {hasValue(vendorname) && (
              <div>
                <p className='activity-card__description'>{vendorname}</p>
              </div>
            )}
            {hasValue(confirmationnumber) && (
              <div className='activity-card__confirmation'>
                {t('services:confirmationLabel', 'Confirmation')}:{' '}
                {confirmationnumber}
              </div>
            )}
          </div>

          {isDesktop && (
            <>
              <div className='activity-card__desktop-preview'>
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:dates', 'Dates')}
                  </p>

                  {startDate && (
                    <div className='activity-card__modal-field__item'>
                      <CalendarBlank size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        <span className='activity-card__modal-field__label'>
                          {t('services:checkIn', 'Check-in')}:
                        </span>{' '}
                        {formatTravelDate(startDate, 'MMM d, yyyy')}{' '}
                        {formatTimeLocale(starttime)}
                      </p>
                    </div>
                  )}

                  {endDate && (
                    <div className='activity-card__modal-field__item'>
                      <CalendarBlank size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        <span className='activity-card__modal-field__label'>
                          {t('services:checkOut', 'Check-out')}:
                        </span>{' '}
                        {formatTravelDate(endDate, 'MMM d, yyyy')}{' '}
                        {formatTimeLocale(endtime)}
                      </p>
                    </div>
                  )}

                  {enddayoffset && (
                    <div className='activity-card__modal-field__item'>
                      <Clock size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        <span className='activity-card__modal-field__label'>
                          {t('services:duration', 'Duration')}:
                        </span>{' '}
                        {enddayoffset} {enddayoffset > 1 ? 'nights' : 'night'}
                      </p>
                    </div>
                  )}
                </div>
                {serviceclass && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:roomType', 'Room Type')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <p className='activity-card__modal-field__value'>
                        {serviceclass}
                      </p>
                    </div>
                  </div>
                )}
                {startlocation && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:address', 'Address')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <MapPin size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        role='button'
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openMaps(startlocation);
                        }}
                        title={t('services:openInMaps', 'Open in Maps')}
                      >
                        <span className='selectable-link'>{startlocation}</span>
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <div className='activity-card__desktop-preview'>
                {vendorphone && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:contact', 'Contact')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <Phone size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        href={`tel:${vendorphone}`}
                        title={t('services:callPhone', 'Call Phone')}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className='selectable-link'>{vendorphone}</span>
                      </a>
                    </div>
                  </div>
                )}

                {vendorlink && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:website', 'Website')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <ArrowSquareOut size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        href={vendorlink}
                        target='_blank'
                        rel='noopener noreferrer'
                        onClick={(e) => e.stopPropagation()}
                        title={t('services:visitWebsite', 'Visit Website')}
                      >
                        {t('services:visitWebsite', 'Visit Website')}
                      </a>
                    </div>
                  </div>
                )}

                {mealsArray.length > 0 && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:mealsIncluded', 'Meals Included')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <ForkKnife size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        {mealsArray.join(', ')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {description && (
                <div className='activity-card__modal-rich-text'>
                  <Editor
                    id={`service-editor-${lineitemid}-description`}
                    content={description}
                    editable={false}
                    isPreviewing={true}
                  />
                </div>
              )}
              {attachments.length > 0 && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:attachedDocument', 'Attached Document')}
                  </p>
                  <div>
                    {attachments.map((attachment, idx) => (
                      <a
                        key={idx}
                        href={attachment.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='activity-card__attachment-link'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FileText size={20} />
                        <span>{attachment.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      );
    }

    // Modal Content
    return (
      <div id={`service-card-hotel-${lineitemid}`}>
        <div>
          {confirmationnumber && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:confirmationLabel', 'Confirmation')}
              </p>
              <div className='activity-card__modal__confirmation'>
                {confirmationnumber}
              </div>
            </div>
          )}

          {vendorname && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:provider', 'Provider')}
              </p>
              <p className='activity-card__modal-field__value'>{vendorname}</p>
            </div>
          )}

          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:dates', 'Dates')}
            </p>

            {startDate && (
              <div className='activity-card__modal-field__item'>
                <CalendarBlank size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  <span className='activity-card__modal-field__label'>
                    {t('services:checkIn', 'Check-in')}:
                  </span>{' '}
                  {formatTravelDate(startDate, 'MMM d, yyyy')}{' '}
                  {formatTimeLocale(starttime)}
                </p>
              </div>
            )}

            {endDate && (
              <div className='activity-card__modal-field__item'>
                <CalendarBlank size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  <span className='activity-card__modal-field__label'>
                    {t('services:checkOut', 'Check-out')}:
                  </span>{' '}
                  {formatTravelDate(endDate, 'MMM d, yyyy')}{' '}
                  {formatTimeLocale(endtime)}
                </p>
              </div>
            )}

            {enddayoffset && (
              <div className='activity-card__modal-field__item'>
                <Clock size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  <span className='activity-card__modal-field__label'>
                    {t('services:duration', 'Duration')}:
                  </span>{' '}
                  {enddayoffset} {enddayoffset > 1 ? 'nights' : 'night'}
                </p>
              </div>
            )}
          </div>

          {serviceclass && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:roomType', 'Room Type')}
              </p>
              <div className='activity-card__modal-field__item'>
                <p className='activity-card__modal-field__value'>
                  {serviceclass}
                </p>
              </div>
            </div>
          )}

          {startlocation && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:address', 'Address')}
              </p>
              <div className='activity-card__modal-field__item'>
                <MapPin size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  role='button'
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openMaps(startlocation);
                  }}
                  title={t('services:openInMaps', 'Open in Maps')}
                >
                  <span className='selectable-link'>{startlocation}</span>
                </a>
              </div>
            </div>
          )}

          {vendorphone && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:contact', 'Contact')}
              </p>
              <div className='activity-card__modal-field__item'>
                <Phone size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  href={`tel:${vendorphone}`}
                  title={t('services:callPhone', 'Call Phone')}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className='selectable-link'>{vendorphone}</span>
                </a>
              </div>
            </div>
          )}

          {vendorlink && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:website', 'Website')}
              </p>
              <div className='activity-card__modal-field__item'>
                <ArrowSquareOut size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  href={vendorlink}
                  target='_blank'
                  rel='noopener noreferrer'
                  title={t('services:visitWebsite', 'Visit Website')}
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('services:visitWebsite', 'Visit Website')}
                </a>
              </div>
            </div>
          )}

          {mealsArray.length > 0 && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:mealsIncluded', 'Meals Included')}
              </p>
              <div className='activity-card__modal-field__item'>
                <ForkKnife size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  {mealsArray.join(', ')}
                </p>
              </div>
            </div>
          )}

          {attachments.length > 0 && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:attachedDocument', 'Attached Document')}
              </p>
              <div>
                {attachments.map((attachment, idx) => (
                  <a
                    key={idx}
                    href={attachment.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='activity-card__attachment-link'
                  >
                    <FileText size={20} />
                    <span>{attachment.name}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {description && (
            <div className='activity-card__modal-rich-text'>
              <Editor
                id={`service-editor-${lineitemid}-description`}
                content={description}
                editable={false}
                isPreviewing={true}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderFlightContent = () => {
    if (isPreview) {
      return (
        <>
          <div className='activity-card__header'>
            <h3 className='activity-card__title'>{title || ''}</h3>
          </div>

          {starttime && (
            <div className='activity-card__time'>
              <Clock size={14} weight='regular' />
              <span>{formatTimeLocale(starttime, timeFormat)}</span>
            </div>
          )}

          <div className='activity-card__description-container'>
            {(vendorname || servicenumber) && (
              <div className='activity-card__description'>
                <p className='activity-card__description'>
                  {vendorname} {servicenumber}
                </p>
              </div>
            )}

            {confirmationnumber && (
              <div className='activity-card__confirmation'>
                {t('services:confirmationLabel', 'Confirmation')}:{' '}
                {confirmationnumber}
              </div>
            )}
          </div>

          {isDesktop && (
            <>
              <div className='activity-card__desktop-preview'>
                {(startDate || starttime || startlocation) && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:departure', 'Departure')}
                    </p>

                    {startDate && (
                      <div className='activity-card__modal-field__item'>
                        <CalendarBlank size={16} color='#667387' />
                        <p className='activity-card__modal-field__value'>
                          {formatTravelDate(startDate, 'MMM d, yyyy')}{' '}
                          {formatTimeLocale(starttime)}
                        </p>
                      </div>
                    )}

                    {startlocation && (
                      <div className='activity-card__modal-field__item'>
                        <MapPin size={16} color='#667387' />
                        <a
                          className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                          role='button'
                          tabIndex={0}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openMaps(startlocation);
                          }}
                          title={t('services:openInMaps', 'Open in Maps')}
                        >
                          <span className='selectable-link'>
                            {startlocation}
                          </span>
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {(endDate || endtime || endlocation) && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:arrival', 'Arrival')}
                    </p>
                    {endDate && (
                      <div className='activity-card__modal-field__item'>
                        <CalendarBlank size={16} color='#667387' />
                        <p className='activity-card__modal-field__value'>
                          {formatTravelDate(endDate, 'MMM d, yyyy')}{' '}
                          {formatTimeLocale(endtime)}{' '}
                          {enddayoffset
                            ? ` (${t('services:nextDay', 'Next Day')})`
                            : ''}
                        </p>
                      </div>
                    )}
                    <div className='activity-card__modal-field__item'>
                      <MapPin size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        role='button'
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openMaps(endlocation);
                        }}
                        title={t('services:openInMaps', 'Open in Maps')}
                      >
                        <span className='selectable-link'>{endlocation}</span>
                      </a>
                    </div>
                  </div>
                )}

                {servicenumber && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:flightNumber', 'Flight Number')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <AirplaneTakeoff size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        {servicenumber}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {description && (
                <div className='activity-card__modal-rich-text'>
                  <Editor
                    id={`service-editor-${lineitemid}-description`}
                    content={description}
                    editable={false}
                    isPreviewing={true}
                  />
                </div>
              )}
              {attachments.length > 0 && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:attachedDocument', 'Attached Document')}
                  </p>
                  <div>
                    {attachments.map((attachment, idx) => (
                      <a
                        key={idx}
                        href={attachment.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='activity-card__attachment-link'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FileText size={20} />
                        <span>{attachment.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      );
    }

    //Modal content for flight
    return (
      <div id={`service-card-flight-${lineitemid}`}>
        <div>
          {confirmationnumber && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:confirmationLabel', 'Confirmation')}
              </p>
              <div className='activity-card__modal__confirmation'>
                {confirmationnumber}
              </div>
            </div>
          )}

          {vendorname && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:airline', 'Airline')}
              </p>
              <p className='activity-card__modal-field__value'>{vendorname}</p>
            </div>
          )}

          {servicenumber && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:flightNumber', 'Flight Number')}
              </p>
              <p className='activity-card__modal-field__value'>
                {servicenumber}
              </p>
            </div>
          )}

          {(startDate || starttime || startlocation) && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:departure', 'Departure')}
              </p>

              {startDate && (
                <div className='activity-card__modal-field__item'>
                  <CalendarBlank size={16} color='#667387' />
                  <p className='activity-card__modal-field__value'>
                    {formatTravelDate(startDate, 'MMM d, yyyy')}{' '}
                    {formatTimeLocale(starttime)}
                  </p>
                </div>
              )}

              {startlocation && (
                <div className='activity-card__modal-field__item'>
                  <MapPin size={16} color='#667387' />
                  <a
                    className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                    role='button'
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openMaps(startlocation);
                    }}
                    title={t('services:openInMaps', 'Open in Maps')}
                  >
                    <span className='selectable-link'>{startlocation}</span>
                  </a>
                </div>
              )}
            </div>
          )}

          {(endDate || endtime || endlocation) && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:arrival', 'Arrival')}
              </p>
              {endDate && (
                <div className='activity-card__modal-field__item'>
                  <CalendarBlank size={16} color='#667387' />
                  <p className='activity-card__modal-field__value'>
                    {formatTravelDate(endDate, 'MMM d, yyyy')}{' '}
                    {formatTimeLocale(endtime)}{' '}
                    {enddayoffset
                      ? ` (${t('services:nextDay', 'Next Day')})`
                      : ''}
                  </p>
                </div>
              )}
              <div className='activity-card__modal-field__item'>
                <MapPin size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  role='button'
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openMaps(endlocation);
                  }}
                  title={t('services:openInMaps', 'Open in Maps')}
                >
                  <span className='selectable-link'>{endlocation}</span>
                </a>
              </div>
            </div>
          )}
          {vendorphone && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:contact', 'Contact')}
              </p>
              <div className='activity-card__modal-field__item'>
                <Phone size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  href={`tel:${vendorphone}`}
                  title={t('services:callPhone', 'Call Phone')}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className='selectable-link'>{vendorphone}</span>
                </a>
              </div>
            </div>
          )}

          {attachments.length > 0 && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:attachedDocument', 'Attached Document')}
              </p>
              <div>
                {attachments.map((attachment, idx) => (
                  <a
                    key={idx}
                    href={attachment.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='activity-card__attachment-link'
                  >
                    <FileText size={20} />
                    <span>{attachment.name}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {description && (
            <div className='activity-card__modal-rich-text'>
              <Editor
                id={`service-editor-${lineitemid}-description`}
                content={description}
                editable={false}
                isPreviewing={true}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderActivityContent = () => {
    if (isPreview) {
      return (
        <>
          <div className='activity-card__header'>
            <h3 className='activity-card__title'>{title || ''}</h3>
          </div>

          {starttime && formatTimeLocale(starttime, timeFormat) && (
            <div className='activity-card__time'>
              <Clock size={14} weight='regular' />
              <span>{formatTimeLocale(starttime, timeFormat)}</span>
            </div>
          )}

          <div className='activity-card__description-container'>
            {vendorname && (
              <div>
                <p className='activity-card__description'>{vendorname}</p>
              </div>
            )}
            {confirmationnumber && (
              <div className='activity-card__confirmation'>
                {t('services:confirmationLabel', 'Confirmation')}:{' '}
                {confirmationnumber}
              </div>
            )}
          </div>

          {isDesktop && (
            <>
              <div className='activity-card__desktop-preview'>
                {durationtime && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:duration', 'Duration')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <Clock size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        {formatDurationDisplay(durationtime)}
                      </p>
                    </div>
                  </div>
                )}
                {startlocation && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:location', 'Location')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <MapPin size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        role='button'
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openMaps(startlocation);
                        }}
                        title={t('services:openInMaps', 'Open in Maps')}
                      >
                        <span className='selectable-link'>{startlocation}</span>
                      </a>
                    </div>
                  </div>
                )}
                {vendorphone && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:contact', ' Contact')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <Phone size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        href={`tel:${vendorphone}`}
                        onClick={(e) => e.stopPropagation()}
                        title={t('services:callPhone', 'Call Phone')}
                      >
                        <span className='selectable-link'>{vendorphone}</span>
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <div className='activity-card__desktop-preview'>
                {vendorlink && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:website', 'Website')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <ArrowSquareOut size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        href={vendorlink}
                        target='_blank'
                        rel='noopener noreferrer'
                        title={t('services:visitWebsite', 'Visit Website')}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t('services:visitWebsite', 'Visit Website')}
                      </a>
                    </div>
                  </div>
                )}
                {mealsArray.length > 0 && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:mealsIncluded', 'Meals Included')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <ForkKnife size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        {mealsArray.join(', ')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {description && (
                <div className='activity-card__modal-rich-text'>
                  <Editor
                    id={`service-editor-${lineitemid}-description`}
                    content={description}
                    editable={false}
                    isPreviewing={true}
                  />
                </div>
              )}
              {attachments.length > 0 && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:attachedDocument', 'Attached Document')}
                  </p>
                  <div>
                    {attachments.map((attachment, idx) => (
                      <a
                        key={idx}
                        href={attachment.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='activity-card__attachment-link'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FileText size={20} />
                        <span>{attachment.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      );
    }

    //Modal content for activity
    return (
      <div id={`service-card-activity-${lineitemid}`}>
        <div>
          {confirmationnumber && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:confirmationLabel', 'Confirmation')}
              </p>
              <div className='activity-card__modal__confirmation'>
                {confirmationnumber}
              </div>
            </div>
          )}

          {vendorname && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:provider', 'Provider')}
              </p>
              <p className='activity-card__modal-field__value'>{vendorname}</p>
            </div>
          )}

          {starttime && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:startTime', 'Start Time')}
              </p>

              <div className='activity-card__modal-field__item'>
                <Clock size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  {formatTimeLocale(starttime, timeFormat)}
                </p>
              </div>
            </div>
          )}

          {durationtime && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:duration', 'Duration')}
              </p>
              <div className='activity-card__modal-field__item'>
                <Clock size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  {formatDurationDisplay(durationtime)}
                </p>
              </div>
            </div>
          )}

          {startlocation && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:address', 'Address')}
              </p>

              <div className='activity-card__modal-field__item'>
                <MapPin size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  role='button'
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openMaps(startlocation);
                  }}
                  title={t('services:openInMaps', 'Open in Maps')}
                >
                  <span className='selectable-link'>{startlocation}</span>
                </a>
              </div>
            </div>
          )}

          {mealsArray.length > 0 && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:mealsIncluded', 'Meals Included')}
              </p>
              <div className='activity-card__modal-field__item'>
                <ForkKnife size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  {mealsArray.join(', ')}
                </p>
              </div>
            </div>
          )}

          {contactname && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:driver', 'Driver')}
              </p>
              <div className='activity-card__modal-field__item'>
                <p className='activity-card__modal-field__value'>
                  {contactname}
                </p>
              </div>
            </div>
          )}

          {vendorphone && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:contact', 'Contact')}
              </p>
              <div className='activity-card__modal-field__item'>
                <Phone size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  href={`tel:${vendorphone}`}
                  title={t('services:callPhone', 'Call Phone')}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className='selectable-link'>{vendorphone}</span>
                </a>
              </div>
            </div>
          )}

          {vendorlink && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:website', 'Website')}
              </p>
              <div className='activity-card__modal-field__item'>
                <ArrowSquareOut size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  href={vendorlink}
                  target='_blank'
                  rel='noopener noreferrer'
                  title={t('services:visitWebsite', 'Visit Website')}
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('services:visitWebsite', 'Visit Website')}
                </a>
              </div>
            </div>
          )}

          {attachments.length > 0 && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:attachedDocument', 'Attached Document')}
              </p>
              <div>
                {attachments.map((attachment, idx) => (
                  <a
                    key={idx}
                    href={attachment.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='activity-card__attachment-link'
                  >
                    <FileText size={20} />
                    <span>{attachment.name}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {description && (
            <div className='activity-card__modal-rich-text'>
              <Editor
                id={`service-editor-${lineitemid}-description`}
                content={description}
                editable={false}
                isPreviewing={true}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRestaurantContent = () => {
    if (isPreview) {
      return (
        <>
          <div className='activity-card__header'>
            <h3 className='activity-card__title'>{title || ''}</h3>
          </div>

          {starttime && formatTimeLocale(starttime, timeFormat) && (
            <div className='activity-card__time'>
              <Clock size={14} weight='regular' />
              <span>{formatTimeLocale(starttime, timeFormat)}</span>
            </div>
          )}

          <div className='activity-card__description-container'>
            {vendorname && (
              <div>
                <p className='activity-card__description'>{vendorname}</p>
              </div>
            )}
            {confirmationnumber && (
              <div className='activity-card__confirmation'>
                {t('services:confirmationLabel', 'Confirmation')}:{' '}
                {confirmationnumber}
              </div>
            )}
          </div>

          {isDesktop && (
            <>
              <div className='activity-card__desktop-preview'>
                {mealsArray.length > 0 && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:mealsIncluded', 'Meals Included')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <ForkKnife size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        {mealsArray.join(', ')}
                      </p>
                    </div>
                  </div>
                )}

                {startlocation && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:location', 'Location')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <MapPin size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        role='button'
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openMaps(startlocation);
                        }}
                        title={t('services:openInMaps', 'Open in Maps')}
                      >
                        <span className='selectable-link'>{startlocation}</span>
                      </a>
                    </div>
                  </div>
                )}

                {vendorphone && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:contact', 'Contact')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <Phone size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        href={`tel:${vendorphone}`}
                        onClick={(e) => e.stopPropagation()}
                        title={t('services:callPhone', 'Call Phone')}
                      >
                        <span className='selectable-link'>{vendorphone}</span>
                      </a>
                    </div>
                  </div>
                )}

                {vendorlink && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:website', 'Website')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <ArrowSquareOut size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        href={vendorlink}
                        target='_blank'
                        rel='noopener noreferrer'
                        onClick={(e) => e.stopPropagation()}
                        title={t('services:visitWebsite', 'Visit Website')}
                      >
                        {t('services:visitWebsite', 'Visit Website')}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {description && (
                <div className='activity-card__modal-rich-text'>
                  <Editor
                    id={`service-editor-${lineitemid}-description`}
                    content={description}
                    editable={false}
                    isPreviewing={true}
                  />
                </div>
              )}
              {attachments.length > 0 && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:attachedDocument', 'Attached Document')}
                  </p>
                  <div>
                    {attachments.map((attachment, idx) => (
                      <a
                        key={idx}
                        href={attachment.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='activity-card__attachment-link'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FileText size={20} />
                        <span>{attachment.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      );
    }

    //Modal content for restaurant
    return (
      <div id={`service-card-restaurant-${lineitemid}`}>
        <div>
          {confirmationnumber && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:confirmationLabel', 'Confirmation')}
              </p>
              <div className='activity-card__modal__confirmation'>
                {confirmationnumber}
              </div>
            </div>
          )}

          {vendorname && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:restaurantName', 'Restaurant Name')}
              </p>
              <p className='activity-card__modal-field__value'>{vendorname}</p>
            </div>
          )}

          {starttime && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:startTime', 'Start Time')}
              </p>

              <div className='activity-card__modal-field__item'>
                <Clock size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  {formatTimeLocale(starttime, timeFormat)}
                </p>
              </div>
            </div>
          )}

          {mealsArray.length > 0 && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:mealsIncluded', 'Meals Included')}
              </p>
              <div className='activity-card__modal-field__item'>
                <ForkKnife size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  {mealsArray.join(', ')}
                </p>
              </div>
            </div>
          )}

          {startlocation && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:address ', 'Address ')}
              </p>

              <div className='activity-card__modal-field__item'>
                <MapPin size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  {startlocation}
                </p>
              </div>
            </div>
          )}

          {vendorphone && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:contact', 'Contact')}
              </p>
              <div className='activity-card__modal-field__item'>
                <Phone size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  href={`tel:${vendorphone}`}
                  title={t('services:callPhone', 'Call Phone')}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className='selectable-link'>{vendorphone}</span>
                </a>
              </div>
            </div>
          )}

          {vendorlink && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:website', 'Website')}
              </p>
              <div className='activity-card__modal-field__item'>
                <ArrowSquareOut size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  href={vendorlink}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={(e) => e.stopPropagation()}
                  title={t('services:visitWebsite', 'Visit Website')}
                >
                  {t('services:visitWebsite', 'Visit Website')}
                </a>
              </div>
            </div>
          )}

          {attachments.length > 0 && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:attachedDocument', 'Attached Document')}
              </p>
              <div>
                {attachments.map((attachment, idx) => (
                  <a
                    key={idx}
                    href={attachment.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='activity-card__attachment-link'
                  >
                    <FileText size={20} />
                    <span>{attachment.name}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {description && (
            <div className='activity-card__modal-rich-text'>
              <Editor
                id={`service-editor-${lineitemid}-description`}
                content={description}
                editable={false}
                isPreviewing={true}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTransferContent = () => {
    if (isPreview) {
      return (
        <>
          <div className='activity-card__header'>
            <h3 className='activity-card__title'>{title || ''}</h3>
          </div>

          {starttime && formatTimeLocale(starttime, timeFormat) && (
            <div className='activity-card__time'>
              <Clock size={14} weight='regular' />
              <span>{formatTimeLocale(starttime, timeFormat)}</span>
            </div>
          )}

          <div className='activity-card__description-container'>
            {vendorname && (
              <div>
                <p className='activity-card__description'>{vendorname}</p>
              </div>
            )}
            {confirmationnumber && (
              <div className='activity-card__confirmation'>
                {t('services:confirmationLabel', 'Confirmation')}:{' '}
                {confirmationnumber}
              </div>
            )}
          </div>

          {isDesktop && (
            <>
              <div className='activity-card__desktop-preview'>
                {(starttime || startlocation) && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:pickup', 'Pickup')}
                    </p>

                    {starttime && (
                      <div className='activity-card__modal-field__item'>
                        <Clock size={16} color='#667387' />
                        <p className='activity-card__modal-field__value'>
                          {formatTimeLocale(starttime, timeFormat)}
                        </p>
                      </div>
                    )}

                    {startlocation && (
                      <div className='activity-card__modal-field__item'>
                        <MapPin size={16} color='#667387' />
                        <a
                          className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                          role='button'
                          tabIndex={0}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openMaps(startlocation);
                          }}
                          title={t('services:openInMaps', 'Open in Maps')}
                        >
                          <span className='selectable-link'>
                            {startlocation}
                          </span>
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {(endtime || endlocation) && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:dropoff', 'Dropoff')}
                    </p>
                    {endtime && (
                      <div className='activity-card__modal-field__item'>
                        <Clock size={16} color='#667387' />
                        <p className='activity-card__modal-field__value'>
                          {formatTimeLocale(endtime, timeFormat)}
                        </p>
                      </div>
                    )}
                    <div className='activity-card__modal-field__item'>
                      <MapPin size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        role='button'
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openMaps(endlocation);
                        }}
                        title={t('services:openInMaps', 'Open in Maps')}
                      >
                        <span className='selectable-link'>{endlocation}</span>
                      </a>
                    </div>
                  </div>
                )}

                {contactname && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:driver', 'Driver')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <p className='activity-card__modal-field__value'>
                        {contactname}
                      </p>
                    </div>
                  </div>
                )}

                {vendorphone && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:contact', 'Contact')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <Phone size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        href={`tel:${vendorphone}`}
                        title={t('services:callPhone', 'Call Phone')}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className='selectable-link'>{vendorphone}</span>
                      </a>
                    </div>
                  </div>
                )}

                {vendorlink && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:website', 'Website')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <ArrowSquareOut size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        href={vendorlink}
                        target='_blank'
                        rel='noopener noreferrer'
                        onClick={(e) => e.stopPropagation()}
                        title={t('services:visitWebsite', 'Visit Website')}
                      >
                        {t('services:visitWebsite', 'Visit Website')}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {description && (
                <div className='activity-card__modal-rich-text'>
                  <Editor
                    id={`service-editor-${lineitemid}-description`}
                    content={description}
                    editable={false}
                    isPreviewing={true}
                  />
                </div>
              )}
              {attachments.length > 0 && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:attachedDocument', 'Attached Document')}
                  </p>
                  <div>
                    {attachments.map((attachment, idx) => (
                      <a
                        key={idx}
                        href={attachment.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='activity-card__attachment-link'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FileText size={20} />
                        <span>{attachment.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      );
    }

    //Modal content for transfer
    return (
      <div id={`service-card-transfer-${lineitemid}`}>
        <div>
          {confirmationnumber && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:confirmationLabel', 'Confirmation')}
              </p>
              <div className='activity-card__modal__confirmation'>
                {confirmationnumber}
              </div>
            </div>
          )}

          {vendorname && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:provider', 'Provider')}
              </p>
              <p className='activity-card__modal-field__value'>{vendorname}</p>
            </div>
          )}
          {(starttime || startlocation) && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:pickup', 'Pickup')}
              </p>

              {starttime && (
                <div className='activity-card__modal-field__item'>
                  <Clock size={16} color='#667387' />
                  <p className='activity-card__modal-field__value'>
                    {formatTimeLocale(starttime, timeFormat)}
                  </p>
                </div>
              )}

              {startlocation && (
                <div className='activity-card__modal-field__item'>
                  <MapPin size={16} color='#667387' />
                  <a
                    className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                    role='button'
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openMaps(startlocation);
                    }}
                    title={t('services:openInMaps', 'Open in Maps')}
                  >
                    <span className='selectable-link'>{startlocation}</span>
                  </a>
                </div>
              )}
            </div>
          )}

          {(endtime || endlocation) && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:dropoff', 'Dropoff')}
              </p>
              {endtime && (
                <div className='activity-card__modal-field__item'>
                  <Clock size={16} color='#667387' />
                  <p className='activity-card__modal-field__value'>
                    {formatTimeLocale(endtime, timeFormat)}
                  </p>
                </div>
              )}
              <div className='activity-card__modal-field__item'>
                <MapPin size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  role='button'
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openMaps(endlocation);
                  }}
                  title={t('services:openInMaps', 'Open in Maps')}
                >
                  <span className='selectable-link'>{endlocation}</span>
                </a>
              </div>
            </div>
          )}

          {contactname && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:driver', 'Driver')}
              </p>
              <div className='activity-card__modal-field__item'>
                <p className='activity-card__modal-field__value'>
                  {contactname}
                </p>
              </div>
            </div>
          )}

          {vendorphone && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:contact', 'Contact')}
              </p>
              <div className='activity-card__modal-field__item'>
                <Phone size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  href={`tel:${vendorphone}`}
                  title={t('services:callPhone', 'Call Phone')}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className='selectable-link'>{vendorphone}</span>
                </a>
              </div>
            </div>
          )}

          {vendorlink && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:website', 'Website')}
              </p>
              <div className='activity-card__modal-field__item'>
                <ArrowSquareOut size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  href={vendorlink}
                  target='_blank'
                  rel='noopener noreferrer'
                  title={t('services:visitWebsite', 'Visit Website')}
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('services:visitWebsite', 'Visit Website')}
                </a>
              </div>
            </div>
          )}

          {mealsArray.length > 0 && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:mealsIncluded', 'Meals Included')}
              </p>
              <div className='activity-card__modal-field__item'>
                <ForkKnife size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  {mealsArray.join(', ')}
                </p>
              </div>
            </div>
          )}

          {attachments.length > 0 && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:attachedDocument', 'Attached Document')}
              </p>
              <div>
                {attachments.map((attachment, idx) => (
                  <a
                    key={idx}
                    href={attachment.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='activity-card__attachment-link'
                  >
                    <FileText size={20} />
                    <span>{attachment.name}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {description && (
            <div className='activity-card__modal-rich-text'>
              <Editor
                id={`service-editor-${lineitemid}-description`}
                content={description}
                editable={false}
                isPreviewing={true}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCarContent = () => {
    if (isPreview) {
      return (
        <>
          <div className='activity-card__header'>
            <h3 className='activity-card__title'>{title || ''}</h3>
          </div>

          <div className='activity-card__description-container'>
            {vendorname && (
              <div>
                <p className='activity-card__description'>{vendorname}</p>
              </div>
            )}
            {confirmationnumber && (
              <div className='activity-card__confirmation'>
                {t('services:confirmationLabel', 'Confirmation')}:{' '}
                {confirmationnumber}
              </div>
            )}
          </div>

          {isDesktop && (
            <>
              <div className='activity-card__desktop-preview'>
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:pickup', 'Pickup')}
                  </p>

                  {startDate && (
                    <div className='activity-card__modal-field__item'>
                      <CalendarBlank size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        {formatTravelDate(startDate, 'MMM d, yyyy')}{' '}
                        {formatTimeLocale(starttime)}
                      </p>
                    </div>
                  )}

                  {startlocation && (
                    <div className='activity-card__modal-field__item'>
                      <MapPin size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        role='button'
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openMaps(startlocation);
                        }}
                        title={t('services:openInMaps', 'Open in Maps')}
                      >
                        <span className='selectable-link'>{startlocation}</span>
                      </a>
                    </div>
                  )}
                </div>

                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:dropoff', 'Dropoff')}
                  </p>

                  {endDate && (
                    <div className='activity-card__modal-field__item'>
                      <CalendarBlank size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        {formatTravelDate(endDate, 'MMM d, yyyy')}{' '}
                        {formatTimeLocale(endtime)}
                      </p>
                    </div>
                  )}
                  {endlocation && (
                    <div className='activity-card__modal-field__item'>
                      <MapPin size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        role='button'
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openMaps(endlocation);
                        }}
                        title={t('services:openInMaps', 'Open in Maps')}
                      >
                        <span className='selectable-link'>{endlocation}</span>
                      </a>
                    </div>
                  )}
                </div>

                {serviceclass && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:vehicleClass', 'Vehicle Class')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <p className='activity-card__modal-field__value'>
                        {serviceclass}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {description && (
                <div className='activity-card__modal-rich-text'>
                  <Editor
                    id={`service-editor-${lineitemid}-description`}
                    content={description}
                    editable={false}
                    isPreviewing={true}
                  />
                </div>
              )}
              {attachments.length > 0 && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:attachedDocument', 'Attached Document')}
                  </p>
                  <div>
                    {attachments.map((attachment, idx) => (
                      <a
                        key={idx}
                        href={attachment.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='activity-card__attachment-link'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FileText size={20} />
                        <span>{attachment.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      );
    }

    //Modal content for car
    return (
      <div id={`service-card-car-${lineitemid}`}>
        <div>
          {confirmationnumber && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:confirmationLabel', 'Confirmation')}
              </p>
              <div className='activity-card__modal__confirmation'>
                {confirmationnumber}
              </div>
            </div>
          )}

          {vendorname && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:rentalCompany', 'Rental Company')}
              </p>
              <p className='activity-card__modal-field__value'>{vendorname}</p>
            </div>
          )}

          {serviceclass && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:vehicleClass', 'Vehicle Class')}
              </p>
              <div className='activity-card__modal-field__item'>
                <p className='activity-card__modal-field__value'>
                  {serviceclass}
                </p>
              </div>
            </div>
          )}

          {(startDate || starttime || startlocation) && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:pickup', 'Pickup')}
              </p>

              {startDate && (
                <div className='activity-card__modal-field__item'>
                  <CalendarBlank size={16} color='#667387' />
                  <p className='activity-card__modal-field__value'>
                    {formatTravelDate(startDate, 'MMM d, yyyy')}{' '}
                    {formatTimeLocale(starttime)}
                  </p>
                </div>
              )}

              {startlocation && (
                <div className='activity-card__modal-field__item'>
                  <MapPin size={16} color='#667387' />
                  <a
                    className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                    role='button'
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openMaps(startlocation);
                    }}
                    title={t('services:openInMaps', 'Open in Maps')}
                  >
                    <span className='selectable-link'>{startlocation}</span>
                  </a>
                </div>
              )}
            </div>
          )}

          {(endDate || endtime || endlocation) && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:dropoff', 'Dropoff')}
              </p>
              {endDate && (
                <div className='activity-card__modal-field__item'>
                  <CalendarBlank size={16} color='#667387' />
                  <p className='activity-card__modal-field__value'>
                    {formatTravelDate(endDate, 'MMM d, yyyy')}{' '}
                    {formatTimeLocale(endtime)}
                  </p>
                </div>
              )}
              <div className='activity-card__modal-field__item'>
                <MapPin size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  role='button'
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openMaps(endlocation);
                  }}
                  title={t('services:openInMaps', 'Open in Maps')}
                >
                  <span className='selectable-link'>{endlocation}</span>
                </a>
              </div>
            </div>
          )}

          {contactname && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:driver', 'Driver')}
              </p>
              <div className='activity-card__modal-field__item'>
                <p className='activity-card__modal-field__value'>
                  {contactname}
                </p>
              </div>
            </div>
          )}

          {vendorphone && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:contact', 'Contact')}
              </p>
              <div className='activity-card__modal-field__item'>
                <Phone size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  href={`tel:${vendorphone}`}
                  title={t('services:callPhone', 'Call Phone')}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className='selectable-link'>{vendorphone}</span>
                </a>
              </div>
            </div>
          )}

          {vendorlink && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:website', 'Website')}
              </p>
              <div className='activity-card__modal-field__item'>
                <ArrowSquareOut size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  href={vendorlink}
                  target='_blank'
                  rel='noopener noreferrer'
                  title={t('services:visitWebsite', 'Visit Website')}
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('services:visitWebsite', 'Visit Website')}
                </a>
              </div>
            </div>
          )}

          {mealsArray.length > 0 && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:mealsIncluded', 'Meals Included')}
              </p>
              <div className='activity-card__modal-field__item'>
                <ForkKnife size={16} color='#667387' />
                <p className='activity-card__modal-field__value'>
                  {mealsArray.join(', ')}
                </p>
              </div>
            </div>
          )}

          {attachments.length > 0 && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:attachedDocument', 'Attached Document')}
              </p>
              <div>
                {attachments.map((attachment, idx) => (
                  <a
                    key={idx}
                    href={attachment.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='activity-card__attachment-link'
                  >
                    <FileText size={20} />
                    <span>{attachment.name}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {description && (
            <div className='activity-card__modal-rich-text'>
              <Editor
                id={`service-editor-${lineitemid}-description`}
                content={description}
                editable={false}
                isPreviewing={true}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTrainContent = () => {
    if (isPreview) {
      return (
        <>
          <div className='activity-card__header'>
            <h3 className='activity-card__title'>{title || ''}</h3>
          </div>

          {starttime && (
            <div className='activity-card__time'>
              <Clock size={14} weight='regular' />
              <span>{formatTimeLocale(starttime, timeFormat)}</span>
            </div>
          )}

          <div className='activity-card__description-container'>
            {(vendorname || servicenumber) && (
              <div className='activity-card__description'>
                <p className='activity-card__description'>{vendorname}</p>
              </div>
            )}
            {confirmationnumber && (
              <div className='activity-card__confirmation'>
                {t('services:confirmationLabel', 'Confirmation')}:{' '}
                {confirmationnumber}
              </div>
            )}
          </div>

          {isDesktop && (
            <>
              <div className='activity-card__desktop-preview'>
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:departure', 'Departure')}
                  </p>

                  {startDate && (
                    <div className='activity-card__modal-field__item'>
                      <CalendarBlank size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        {formatTravelDate(startDate, 'MMM d, yyyy')}{' '}
                        {formatTimeLocale(starttime)}
                      </p>
                    </div>
                  )}

                  {startlocation && (
                    <div className='activity-card__modal-field__item'>
                      <MapPin size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        role='button'
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openMaps(startlocation);
                        }}
                        title={t('services:openInMaps', 'Open in Maps')}
                      >
                        <span className='selectable-link'>{startlocation}</span>
                      </a>
                    </div>
                  )}
                </div>

                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:arrival', 'Arrival')}
                  </p>

                  {endDate && (
                    <div className='activity-card__modal-field__item'>
                      <CalendarBlank size={16} color='#667387' />
                      <p className='activity-card__modal-field__value'>
                        {formatTravelDate(endDate, 'MMM d, yyyy')}{' '}
                        {formatTimeLocale(endtime)}{' '}
                        {enddayoffset
                          ? ` (${t('services:nextDay', 'Next Day')})`
                          : ''}
                      </p>
                    </div>
                  )}
                  {endlocation && (
                    <div className='activity-card__modal-field__item'>
                      <MapPin size={16} color='#667387' />
                      <a
                        className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                        role='button'
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openMaps(endlocation);
                        }}
                        title={t('services:openInMaps', 'Open in Maps')}
                      >
                        <span className='selectable-link'>{endlocation}</span>
                      </a>
                    </div>
                  )}
                </div>

                {servicenumber && (
                  <div className='activity-card__modal-field'>
                    <p className='activity-card__modal-field__title'>
                      {t('services:trainNumber', 'Train Number')}
                    </p>
                    <div className='activity-card__modal-field__item'>
                      <p className='activity-card__modal-field__value'>
                        {servicenumber}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {description && (
                <div className='activity-card__modal-rich-text'>
                  <Editor
                    id={`service-editor-${lineitemid}-description`}
                    content={description}
                    editable={false}
                    isPreviewing={true}
                  />
                </div>
              )}
              {attachments.length > 0 && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:attachedDocument', 'Attached Document')}
                  </p>
                  <div>
                    {attachments.map((attachment, idx) => (
                      <a
                        key={idx}
                        href={attachment.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='activity-card__attachment-link'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FileText size={20} />
                        <span>{attachment.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      );
    }

    //Modal content for train
    return (
      <div id={`service-card-car-${lineitemid}`}>
        <div>
          {confirmationnumber && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:confirmationLabel', 'Confirmation')}
              </p>
              <div className='activity-card__modal__confirmation'>
                {confirmationnumber}
              </div>
            </div>
          )}

          {vendorname && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:rentalCompany', 'Company')}
              </p>
              <p className='activity-card__modal-field__value'>{vendorname}</p>
            </div>
          )}

          {(startDate || starttime || startlocation) && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:departure', 'Departure')}
              </p>

              {startDate && (
                <div className='activity-card__modal-field__item'>
                  <CalendarBlank size={16} color='#667387' />
                  <p className='activity-card__modal-field__value'>
                    {formatTravelDate(startDate, 'MMM d, yyyy')}{' '}
                    {formatTimeLocale(starttime)}
                  </p>
                </div>
              )}

              {startlocation && (
                <div className='activity-card__modal-field__item'>
                  <MapPin size={16} color='#667387' />
                  <a
                    className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                    role='button'
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openMaps(startlocation);
                    }}
                    title={t('services:openInMaps', 'Open in Maps')}
                  >
                    <span className='selectable-link'>{startlocation}</span>
                  </a>
                </div>
              )}
            </div>
          )}

          {(endDate || endtime || endlocation) && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:arrival', 'Arrival')}
              </p>
              {endDate && (
                <div className='activity-card__modal-field__item'>
                  <CalendarBlank size={16} color='#667387' />
                  <p className='activity-card__modal-field__value'>
                    {formatTravelDate(endDate, 'MMM d, yyyy')}{' '}
                    {formatTimeLocale(endtime)}{' '}
                    {enddayoffset
                      ? ` (${t('services:nextDay', 'Next Day')})`
                      : ''}
                  </p>
                </div>
              )}
              <div className='activity-card__modal-field__item'>
                <MapPin size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  role='button'
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openMaps(endlocation);
                  }}
                  title={t('services:openInMaps', 'Open in Maps')}
                >
                  <span className='selectable-link'>{endlocation}</span>
                </a>
              </div>
            </div>
          )}

          {attachments.length > 0 && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:attachedDocument', 'Attached Document')}
              </p>
              <div>
                {attachments.map((attachment, idx) => (
                  <a
                    key={idx}
                    href={attachment.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='activity-card__attachment-link'
                  >
                    <FileText size={20} />
                    <span>{attachment.name}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {description && (
            <div className='activity-card__modal-rich-text'>
              <Editor
                id={`service-editor-${lineitemid}-description`}
                content={description}
                editable={false}
                isPreviewing={true}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderInfoContent = () => {
    if (isPreview) {
      return (
        <>
          <div className='activity-card__header'>
            <h3 className='activity-card__title'>{title || ''}</h3>
          </div>

          {description && (
            <div className='activity-card__description-container'>
              <Editor
                id={`service-editor-${lineitemid}-description-preview`}
                content={description}
                editable={false}
                isPreviewing={true}
              />
            </div>
          )}

          {vendorlink && (
            <div className='activity-card__link'>
              <a
                href={vendorlink}
                target='_blank'
                rel='noopener noreferrer'
                className='activity-card__link-text'
              >
                {t('services:link', 'Link')}
              </a>
            </div>
          )}
        </>
      );
    }

    return (
      <div className='activity-card'>
        <div className='activity-card__modal'>
          <h2 className='activity-card__modal-title'>{title}</h2>

          {vendorlink && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:website', 'Website')}
              </p>
              <div className='activity-card__modal-field__item'>
                <ArrowSquareOut size={16} color='#667387' />
                <a
                  className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                  href={vendorlink}
                  target='_blank'
                  rel='noopener noreferrer'
                  title={t('services:visitWebsite', 'Visit Website')}
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('services:visitWebsite', 'Visit Website')}
                </a>
              </div>
            </div>
          )}

          {attachments.length > 0 && (
            <div className='activity-card__modal-field'>
              <p className='activity-card__modal-field__title'>
                {t('services:attachedDocument', 'Attached Document')}
              </p>
              <div>
                {attachments.map((attachment, idx) => (
                  <a
                    key={idx}
                    href={attachment.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='activity-card__attachment-link'
                  >
                    <FileText size={20} />
                    <span>{attachment.name}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {description && (
            <div className='activity-card__modal-rich-text'>
              <Editor
                id={`service-editor-${lineitemid}-description`}
                content={description}
                editable={false}
                isPreviewing={true}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  // Main render
  return renderServiceContent();
};

TravelerAppLineItemDisplay.propTypes = {
  lineItem: PropTypes.object.isRequired,
  isPreview: PropTypes.bool,
  isDesktop: PropTypes.bool,
  isCheckout: PropTypes.bool
};

export default TravelerAppLineItemDisplay;
